import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCanvasStore } from '@refly/stores';
import getClient from '@refly-packages/ai-workspace-common/requests/proxiedRequest';
import type { WorkflowExecution } from '@refly/openapi-schema';
import {
  selectCanvasNodeExecutions,
  type CanvasNodeExecution,
} from '../../utils/canvas-node-executions';

// Canvas nodes can belong to different concurrent workflow runs. A single run's
// copied upstream snapshots must never replace the actual state of another run.
export function useCanvasNodeExecutions(canvasId: string) {
  const history = useRef({ canvasId, runs: new Map<string, WorkflowExecution>() });
  const { data } = useQuery({
    queryKey: ['canvas-node-executions', canvasId],
    enabled: !!canvasId && !location.pathname.startsWith('/share/'),
    refetchInterval: 2000,
    queryFn: async () => {
      if (history.current.canvasId !== canvasId) {
        history.current = { canvasId, runs: new Map() };
      }
      const completed = history.current.runs;
      const client = getClient();
      const ids = new Set<string>();
      const summaries = new Map<string, WorkflowExecution>();
      const hasActiveNodes = (run: WorkflowExecution) =>
        (run.nodeExecutions ?? []).some((node) =>
          ['executing', 'waiting_external', 'waiting_user'].includes(node.status),
        );
      for (const cached of completed.values()) {
        if (!['finish', 'failed'].includes(cached.status) || hasActiveNodes(cached))
          ids.add(cached.executionId);
      }
      for (const status of ['executing', 'init', 'waiting_user'] as const) {
        for (let page = 1; ; page++) {
          const response = await client.listWorkflowExecutions({
            query: { canvasId, status, page, pageSize: 100 },
          });
          if (response.error) throw new Error('获取节点运行状态失败');
          const runs = response.data?.data ?? [];
          for (const run of runs) {
            ids.add(run.executionId);
            summaries.set(run.executionId, run);
          }
          if (runs.length < 100) break;
        }
      }
      const recent = await client.listWorkflowExecutions({
        query: { canvasId, pageSize: 20, order: 'creationDesc' },
      });
      if (recent.error) throw new Error('获取节点运行状态失败');
      for (const run of recent.data?.data ?? []) {
        ids.add(run.executionId);
        summaries.set(run.executionId, run);
      }
      // Include runs that just left the active list so their terminal state is read.
      for (const row of (useCanvasStore.getState().canvasNodeExecutions[canvasId] ??
        []) as CanvasNodeExecution[]) {
        if (row.executionId) ids.add(row.executionId);
      }
      const details = await Promise.all(
        [...ids].map(async (executionId) => {
          const cached = completed.get(executionId);
          const summary = summaries.get(executionId);
          if (
            cached &&
            ['finish', 'failed'].includes(cached.status) &&
            !hasActiveNodes(cached) &&
            summary?.updatedAt === cached.updatedAt &&
            summary?.status === cached.status
          )
            return cached;
          const response = await client.getWorkflowDetail({ query: { executionId } });
          if (response.error || !response.data?.data) throw new Error('获取节点运行状态失败');
          return response.data.data;
        }),
      );
      for (const detail of details) completed.set(detail.executionId, detail);
      const selected = selectCanvasNodeExecutions(
        [...completed.values()].flatMap((detail) =>
          (detail.nodeExecutions ?? []).map((node) => ({
            ...node,
            executionId: detail.executionId,
          })),
        ),
      );
      const retained = new Set([...ids, ...selected.map((node) => node.executionId)]);
      for (const id of completed.keys()) if (!retained.has(id)) completed.delete(id);
      return selected;
    },
  });
  useEffect(() => {
    if (data) useCanvasStore.getState().setCanvasNodeExecutions(canvasId, data);
  }, [canvasId, data]);
}
